import React from "react";
import styled from "styled-components";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";
import { getPriceIdFrom } from "../AccountDetails/Subscription/subscriptionUtils";

/* ── Tokens ─────────────────────────────────────────────────── */
const C = {
    surface: "#ffffff",
    border:  "#e2e8f0",
    text:    "#0f172a",
    muted:   "#64748b",
    indigo:  "#6366f1",
    indigoD: "#4f46e5",
    bg:      "#f8fafc",
};

/* ── Card ────────────────────────────────────────────────────── */
const Card = styled.div`
    background: ${({ $featured }) => $featured ? C.indigo : C.surface};
    border: 2px solid ${({ $featured }) => $featured ? C.indigo : C.border};
    border-radius: 16px;
    padding: 28px 24px;
    display: flex;
    flex-direction: column;
    position: relative;
    font-family: "Inter", system-ui, sans-serif;
    transition: box-shadow 150ms;
    &:hover { box-shadow: 0 8px 32px rgba(0,0,0,0.10); }
`;

const Badge = styled.div`
    position: absolute;
    top: -13px;
    left: 50%;
    transform: translateX(-50%);
    background: #f59e0b;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    padding: 3px 12px;
    border-radius: 20px;
    letter-spacing: 0.5px;
    white-space: nowrap;
`;

const PlanName = styled.div`
    font-size: 1rem;
    font-weight: 700;
    color: ${({ $featured }) => $featured ? "rgba(255,255,255,0.9)" : C.muted};
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-size: 0.75rem;
`;

const PriceRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 4px;
`;

const Amount = styled.span`
    font-size: 2.4rem;
    font-weight: 800;
    line-height: 1;
    color: ${({ $featured }) => $featured ? "#ffffff" : C.text};
`;

const Period = styled.span`
    font-size: 0.9rem;
    color: ${({ $featured }) => $featured ? "rgba(255,255,255,0.7)" : C.muted};
`;

const TrialNote = styled.div`
    font-size: 0.78rem;
    color: ${({ $featured }) => $featured ? "rgba(255,255,255,0.65)" : C.muted};
    margin-bottom: 20px;
    min-height: 1.2em;
`;

const Divider = styled.div`
    height: 1px;
    background: ${({ $featured }) => $featured ? "rgba(255,255,255,0.2)" : C.border};
    margin-bottom: 20px;
`;

const FeatureList = styled.ul`
    list-style: none;
    margin: 0 0 28px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
`;

const Feature = styled.li`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 0.85rem;
    color: ${({ $featured }) => $featured ? "rgba(255,255,255,0.9)" : "#374151"};
`;

const Check = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 17px;
    height: 17px;
    border-radius: 50%;
    background: ${({ $featured }) => $featured ? "rgba(255,255,255,0.25)" : "#f0fdf4"};
    color: ${({ $featured }) => $featured ? "#ffffff" : "#16a34a"};
    font-size: 0.65rem;
    font-weight: 800;
    flex-shrink: 0;
    margin-top: 1px;
`;

const ActionBtn = styled.button`
    width: 100%;
    padding: 11px;
    border-radius: 9px;
    font-size: 0.9rem;
    font-weight: 700;
    font-family: "Inter", system-ui, sans-serif;
    cursor: ${({ disabled }) => disabled ? "default" : "pointer"};
    transition: background 120ms, opacity 120ms;
    border: ${({ $featured, $outline }) =>
        $featured ? "none" :
        $outline   ? "2px solid #e2e8f0" : "none"};
    background: ${({ $featured, $outline }) =>
        $featured ? "#ffffff" :
        $outline   ? "transparent" : C.indigo};
    color: ${({ $featured, $outline }) =>
        $featured ? C.indigo :
        $outline   ? C.muted : "#ffffff"};
    opacity: ${({ disabled }) => disabled ? 0.55 : 1};

    &:hover:not(:disabled) {
        background: ${({ $featured, $outline }) =>
            $featured ? "#f0f0ff" :
            $outline   ? "#f8fafc" : C.indigoD};
    }
`;

/* ── Helpers ─────────────────────────────────────────────────── */
const featuredGroups = new Set(["pro"]);

function isFeatured(product) {
    return featuredGroups.has(product.featuresGroup);
}

export default function ProductCard({ product, priceInterval, cardSizes, action }) {
    const history            = useHistory();
    const billingInformation = useSelector(s => s.login.userDetails.billingInformation);
    const isLoggedIn         = useSelector(s => s.login.userDetails.isLoggedIn);
    const featured           = isFeatured(product);

    const price = getPriceForProduct(product, priceInterval);
    if (!price) return null;

    const amount       = (price.unitAmountDecimal / 100).toFixed(2).replace(/\.00$/, "");
    const isFree       = price.unitAmountDecimal === 0;
    const trialDays    = price.recurring?.trialPeriodDays;
    const currentPriceId = billingInformation ? getPriceIdFrom(billingInformation) : null;
    const isOwned      = isLoggedIn && currentPriceId === price.id;
    const isCancelling = isLoggedIn && isOwned === false && isFree &&
        billingInformation?.subscription?.plan?.price > 0;

    function handleClick() {
        if (isOwned) return;
        history.push("/checkout?priceId=" + price.id);
    }

    function btnLabel() {
        if (isOwned) return "Current plan";
        if (isCancelling) return "Cancel subscription";
        if (isFree) return "Get started free";
        return "Get started";
    }

    const cardAction = action || (
        <ActionBtn
            $featured={featured}
            $outline={!featured && !isFree}
            disabled={isOwned}
            onClick={handleClick}
        >
            {btnLabel()}
        </ActionBtn>
    );

    return (
        <Card $featured={featured}>
            {featured && <Badge>Most popular</Badge>}

            <PlanName $featured={featured}>{product.name.replace(/plan/gi, "").trim()}</PlanName>

            <PriceRow>
                <Amount $featured={featured}>£{amount}</Amount>
                <Period $featured={featured}>/ {priceInterval === "month" ? "mo" : "yr"}</Period>
            </PriceRow>

            <TrialNote $featured={featured}>
                {trialDays ? `${trialDays}-day free trial` : " "}
            </TrialNote>

            <Divider $featured={featured} />

            <FeatureList>
                {product.features.map((f, i) => (
                    <Feature key={i} $featured={featured}>
                        <Check $featured={featured}>✓</Check>
                        {f.label}
                    </Feature>
                ))}
            </FeatureList>

            {cardAction}
        </Card>
    );
}

function getPriceForProduct(product, interval) {
    return product.prices.find(p => p.recurring?.interval === interval);
}
